import { logger } from 'src/app/common/services/logging/logging.service';
import { VoiceRecognitionService } from './voice-recognition.service';
import { MEAPIService } from '../../services/api/me-api.service';
import { AsyncSubject } from 'rxjs';
import { BasicAsyncResponseSuccessHandler } from '../../services/api/me-api-response';

export class VoiceRecognitionServerService extends VoiceRecognitionService {

    private mediaRecorder!: MediaRecorder;
    private recordedChunks: Blob[] = [];
    private stream!: MediaStream;
    public static readonly API_SPEECH_TO_TEXT = MEAPIService.BASE_URL + '/customer/voiceCommand/transcribe';

    constructor(private srvApi: MEAPIService) { 
        super();
        let isSupport = ('MediaRecorder' in window);
        this.isDeviceSupport$.next(isSupport);
        this.isDeviceSupport$.complete();
    }

    override init() {
    }

    override async start() {
        if ((this.isListening$.value)) {
            logger.error("start was called but is listening is true");
            return;
        }

        // await this.playAudio();

        this.message$.next("");
        this.isListening$.next(true);

        this.startRecording();
    }

    override stop() {
        if (!(this.isListening$.value)) {
            logger.error("stop was called but is listening is false");
            return;
        }

        this.stopRecording().then((blob) => {
            this.transcribe(blob).subscribe((speech: Speech|null) => {
                let message = (speech?.response || '');
                this.message$.next(message);

                this.isCompleted$.next(true);
                this.isCompleted$.complete();
            });
        });

        this.isListening$.next(false);

    }

    private async startRecording() {
        this.stream = await navigator.mediaDevices.getUserMedia({
            audio: {
                sampleRate: 16000,  // Set sample rate for better compatibility
                channelCount: 1,    // Mono audio
                echoCancellation: true,
                noiseSuppression: true
            }
        });
        this.recordedChunks = [];

        // Try to use WAV format first
        if (MediaRecorder.isTypeSupported('audio/wav')) {
            this.mediaRecorder = new MediaRecorder(this.stream, {
                mimeType: 'audio/wav'
            });
        } else if (MediaRecorder.isTypeSupported('audio/webm;codecs=pcm')) {
            // Alternative: WebM with PCM codec (uncompressed)
            this.mediaRecorder = new MediaRecorder(this.stream, {
                mimeType: 'audio/webm;codecs=pcm'
            });
        } else {
            // Fallback to default (usually WebM with Opus)
            this.mediaRecorder = new MediaRecorder(this.stream);
        }

        this.mediaRecorder.ondataavailable = (event) => {
            if (event.data.size > 0) {
                this.recordedChunks.push(event.data);
            }
        };

        this.mediaRecorder.start();
    }


    public stopRecording(): Promise<Blob> {
        return new Promise((resolve, reject) => {
            if (!this.mediaRecorder || this.mediaRecorder.state === 'inactive') {
                return reject('MediaRecorder is not active');
            }

            this.mediaRecorder.onstop = async () => {
                // Determine the MIME type based on what was actually recorded
                const mimeType = this.mediaRecorder.mimeType || 'audio/wav';

                if (mimeType.includes('wav')) {
                    // Direct WAV recording
                    const audioBlob = new Blob(this.recordedChunks, { type: 'audio/wav' });
                    if (audioBlob.size === 0)
                        return reject('No audio data recorded');
                    resolve(audioBlob);
                } else {
                    // Convert WebM/other formats to WAV
                    const webmBlob = new Blob(this.recordedChunks, { type: mimeType });
                    if (webmBlob.size === 0)
                        return reject('No audio data recorded');

                    try {
                        const wavBlob = await this.convertToWav(webmBlob);
                        resolve(wavBlob);
                    } catch (error) {
                        // If conversion fails, send original blob
                        console.warn('WAV conversion failed, sending original format:', error);
                        resolve(webmBlob);
                    }
                }
            };

            setTimeout(() => {
                if (this.stream) {
                    this.stream.getTracks().forEach(track => track.stop());
                }
            }, 0);

            this.mediaRecorder.stop();
        });
    }


    private async convertToWav(blob: Blob): Promise<Blob> {
        return new Promise((resolve, reject) => {
            const audioContext = new (window.AudioContext || (window as any).webkitAudioContext)();
            const fileReader = new FileReader();

            fileReader.onload = async (e) => {
                try {
                    const arrayBuffer = e.target?.result as ArrayBuffer;
                    const audioBuffer = await audioContext.decodeAudioData(arrayBuffer);

                    // Convert to WAV format
                    const wavBuffer = this.audioBufferToWav(audioBuffer);
                    const wavBlob = new Blob([wavBuffer], { type: 'audio/wav' });

                    resolve(wavBlob);
                } catch (error) {
                    reject(error);
                }
            };

            fileReader.onerror = () => reject(new Error('Failed to read audio blob'));
            fileReader.readAsArrayBuffer(blob);
        });
    }

    private audioBufferToWav(buffer: AudioBuffer): ArrayBuffer {
        const length = buffer.length;
        const numberOfChannels = buffer.numberOfChannels;
        const sampleRate = buffer.sampleRate;
        const bytesPerSample = 2; // 16-bit
        const blockAlign = numberOfChannels * bytesPerSample;
        const byteRate = sampleRate * blockAlign;
        const dataSize = length * blockAlign;
        const bufferSize = 44 + dataSize;

        const arrayBuffer = new ArrayBuffer(bufferSize);
        const view = new DataView(arrayBuffer);

        // WAV header
        const writeString = (offset: number, string: string) => {
            for (let i = 0; i < string.length; i++) {
                view.setUint8(offset + i, string.charCodeAt(i));
            }
        };

        writeString(0, 'RIFF');
        view.setUint32(4, bufferSize - 8, true);
        writeString(8, 'WAVE');
        writeString(12, 'fmt ');
        view.setUint32(16, 16, true); // PCM format
        view.setUint16(20, 1, true); // Audio format (1 = PCM)
        view.setUint16(22, numberOfChannels, true);
        view.setUint32(24, sampleRate, true);
        view.setUint32(28, byteRate, true);
        view.setUint16(32, blockAlign, true);
        view.setUint16(34, 16, true); // Bits per sample
        writeString(36, 'data');
        view.setUint32(40, dataSize, true);

        // Convert audio data
        let offset = 44;
        for (let i = 0; i < length; i++) {
            for (let channel = 0; channel < numberOfChannels; channel++) {
                const sample = Math.max(-1, Math.min(1, buffer.getChannelData(channel)[i]));
                view.setInt16(offset, sample < 0 ? sample * 0x8000 : sample * 0x7FFF, true);
                offset += 2;
            }
        }

        return arrayBuffer;
    }

    private transcribe(blob: Blob): AsyncSubject<Speech | null> {

        let formData: FormData = new FormData();
        formData.append("file", blob);

        let ret = new AsyncSubject<Speech | null>();
        this.srvApi.FormDataPost(VoiceRecognitionServerService.API_SPEECH_TO_TEXT, formData, [new BasicAsyncResponseSuccessHandler<Speech | null>(ret)]).subscribe((isSuccess: boolean) => {
            if (!isSuccess) {
                ret.next(null);
                ret.complete();
            }
        });
        return ret;
    }
}

export interface Speech {
    response: string;
}

